import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ValuationComparisonChart, ValuationChartData, Stack, Text } from '@popety-io/ui'
import { EstimateLogValuation, MultiProviderValuation, ValuationSource } from '@popety-io/types'
import { useAutoValuationData } from '../Valuations/hooks/useAutoValuationData'
import { useManualValuationData } from '../Valuations/hooks/useManualValuationData'
import { useClearMapSection } from '../../hooks'
import { usePublicPricePerSqm } from '../../contexts'
import { getPublicFinalValuation } from './finalValuation'
import {
  SummaryContainer,
  SectionCard,
  SectionTitle,
  ChartContainer,
  FinalValuationCard,
  ValuationValue,
  ValuationRange,
  NoDataMessage,
  PricePerSqmLine,
} from './SummaryTab.styles'

interface SummaryTabProps {
  estimateData: any
}

// Map ValuationSource to provider color
const getProviderColor = (source: ValuationSource): string => {
  switch (source) {
    case ValuationSource.POPETY_IO:
      return '#0294df'
    case ValuationSource.PRICE_HUBBLE:
      return '#014EFB'
    case ValuationSource.WUEST_PARTNER:
      return '#332F62'
    case ValuationSource.MANUAL:
      return '#F38181'
    default:
      return '#666666'
  }
}

const getProviderName = (source: ValuationSource): string => {
  switch (source) {
    case ValuationSource.POPETY_IO:
      return 'Popety.io'
    case ValuationSource.PRICE_HUBBLE:
      return 'PriceHubble'
    case ValuationSource.WUEST_PARTNER:
      return 'Wüest Partner'
    case ValuationSource.MANUAL:
      return 'Manual'
    default:
      return 'Unknown'
  }
}

const formatCurrency = (value: number): string => {
  return `CHF ${value?.toLocaleString('de-CH')}`
}

export const SummaryTab: FC<SummaryTabProps> = ({ estimateData }) => {
  const { t } = useTranslation()

  // Price-per-m² context (session-only, not persisted)
  const { show, areas, resolveArea } = usePublicPricePerSqm()
  const basisArea = resolveArea(areas)

  // Clear map section data when viewing summary
  useClearMapSection()

  // Get log_valuation data from estimate
  const logValuation: EstimateLogValuation | MultiProviderValuation | null =
    estimateData?.log_valuation ?? null

  // Parse AVM valuations data using existing hook
  const { valuations: avmValuations } = useAutoValuationData(logValuation)

  // Parse manual valuations from estimateData.price
  const { valuations: manualValuations } = useManualValuationData(
    estimateData?.price ?? null,
    logValuation
  )

  // Get final valuation from value_edited
  const finalValuation = useMemo(() => {
    return getPublicFinalValuation(estimateData?.value_edited)
  }, [estimateData?.value_edited])

  // Calculate listings comparables valuation from saved listings
  const listingsComparablesValuation = useMemo(() => {
    const savedListings = estimateData?.value_edited?.savedListings
    if (!savedListings || savedListings.length === 0) return null

    const prices = savedListings
      .map((l: any) => l.price)
      .filter((p: any): p is number => typeof p === 'number' && p > 0)

    if (prices.length === 0) return null

    const total = prices.reduce((sum: number, p: number) => sum + p, 0)
    const avg = total / prices.length
    const sortedPrices = [...prices].sort((a, b) => a - b)

    return {
      id: 'listings-comparables',
      name: t('estimate.summary.listingsComparables'),
      source: ValuationSource.MANUAL,
      value: Math.round(avg),
      range: {
        min: sortedPrices[0],
        max: sortedPrices[sortedPrices.length - 1],
      },
      confidence: 2,
      color: '#9B59B6',
    }
  }, [estimateData?.value_edited?.savedListings, t])

  // Calculate transactions comparables valuation from saved transactions
  const transactionsComparablesValuation = useMemo(() => {
    const savedTransactions = estimateData?.value_edited?.savedTransactions
    if (!savedTransactions || savedTransactions.length === 0) return null

    const prices = savedTransactions
      .map((t: any) => t.transaction_total_price)
      .filter((p: any): p is number => typeof p === 'number' && p > 0)

    if (prices.length === 0) return null

    const total = prices.reduce((sum: number, p: number) => sum + p, 0)
    const avg = total / prices.length
    const sortedPrices = [...prices].sort((a, b) => a - b)

    return {
      id: 'transactions-comparables',
      name: t('estimate.summary.transactionsComparables'),
      source: ValuationSource.MANUAL,
      value: Math.round(avg),
      range: {
        min: sortedPrices[0],
        max: sortedPrices[sortedPrices.length - 1],
      },
      confidence: 2,
      color: '#E67E22',
    }
  }, [estimateData?.value_edited?.savedTransactions, t])

  // Get hidden valuation IDs from value_edited
  const hiddenValuationIds: string[] = useMemo(
    () => estimateData?.value_edited?.hiddenValuationIds || [],
    [estimateData?.value_edited?.hiddenValuationIds]
  )

  // Combine all valuations and filter out hidden ones
  const allValuations = useMemo(() => {
    const valuations = [...avmValuations, ...manualValuations]
    if (listingsComparablesValuation) {
      valuations.push(listingsComparablesValuation)
    }
    if (transactionsComparablesValuation) {
      valuations.push(transactionsComparablesValuation)
    }
    return valuations.filter((valuation) => !hiddenValuationIds.includes(valuation.id))
  }, [
    avmValuations,
    manualValuations,
    listingsComparablesValuation,
    transactionsComparablesValuation,
    hiddenValuationIds,
  ])

  // Transform valuations to chart data format
  const chartData: ValuationChartData[] = useMemo(() => {
    return allValuations.map((valuation) => ({
      source: valuation.source,
      name: valuation.name || getProviderName(valuation.source),
      value: valuation.value,
      confidenceLow: valuation.range?.min,
      confidenceHigh: valuation.range?.max,
      color: valuation.color || getProviderColor(valuation.source),
      area: basisArea,
    }))
  }, [allValuations, basisArea])

  return (
    <SummaryContainer>
      {/* Final Valuation Section */}
      <SectionCard>
        <SectionTitle>{t('estimate.summary.finalValuation')}</SectionTitle>
        <FinalValuationCard>
          {finalValuation?.value ? (
            <Stack direction="vertical" spacing={8}>
              <Text variant="caption" color="secondary">
                {t('estimate.summary.finalValue')}
              </Text>
              <ValuationValue>{formatCurrency(finalValuation.value)}</ValuationValue>
              <PricePerSqmLine>
                {show && basisArea && basisArea > 0 && (
                  <Text variant="body2" color="secondary">
                    {formatCurrency(Math.round(finalValuation.value / basisArea))} /m²
                  </Text>
                )}
              </PricePerSqmLine>
              {(finalValuation.range?.min || finalValuation.range?.max) && (
                <ValuationRange>
                  {t('estimate.summary.range', {
                    min: finalValuation.range?.min ? formatCurrency(finalValuation.range.min) : '-',
                    max: finalValuation.range?.max ? formatCurrency(finalValuation.range.max) : '-',
                  })}
                </ValuationRange>
              )}
              {show &&
                basisArea &&
                basisArea > 0 &&
                finalValuation.range?.min &&
                finalValuation.range?.max && (
                  <Text variant="caption" color="secondary">
                    {formatCurrency(Math.round(finalValuation.range.min / basisArea))} /m² –{' '}
                    {formatCurrency(Math.round(finalValuation.range.max / basisArea))} /m²
                  </Text>
                )}
            </Stack>
          ) : (
            <NoDataMessage>
              <Text variant="body1" color="secondary">
                {t('estimate.summary.noFinalValuation')}
              </Text>
            </NoDataMessage>
          )}
        </FinalValuationCard>
      </SectionCard>

      {/* Valuation Comparison Chart */}
      {chartData.length > 0 && (
        <SectionCard>
          <SectionTitle>{t('estimate.summary.valuationComparison')}</SectionTitle>
          <ChartContainer>
            <ValuationComparisonChart
              data={chartData}
              currency="CHF"
              finalValuation={finalValuation || undefined}
              finalValuationLabel={t('estimate.summary.finalValue')}
              showPricePerSqm={show}
            />
          </ChartContainer>
        </SectionCard>
      )}
    </SummaryContainer>
  )
}
